import { NolimitApplication } from '@nolimitcity/slot-launcher/bin/NolimitApplication';
import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { gsap } from 'gsap';
import { Beheaded } from '../../Beheaded';
import { BeheadedEngineManager } from '../../reels/BeheadedEngineManager';
import { SoulWild } from './SoulWild';
import { SoulWildIndicator } from './SoulWildIndicator';
import { IDimOrientation, ResizeHelper } from '../../../core/utils/ResizeHelper';
import { Point } from 'pixi.js';
import { GameSetting } from '../../../core/setting/GameSetting';
import { ArrayHelper } from '../../../core/utils/ArrayHelper';
import { SoundConfig } from '../../config/sound/SoundConfig';
import { SoundManager } from '../../config/sound/SoundManager';

export class SoulWildController extends PIXI.Container implements StageLayer {
    public levelThresholds: number[] = [4, 6, 8];

    protected readonly dim: IDimOrientation = {
        landscape: {
            scale: new Point(1, 1),
            position: new Point(132, 130),
        },
        portrait: {
            scale: new Point(1, 1),
            position: new Point(134, 120),
        },
    };

    private souldWildsIndicators: SoulWildIndicator[] = [];
    private soulWild: SoulWild;
    private isSoulWild: boolean;

    public get SWIndicators(): SoulWildIndicator[] {
        return this.souldWildsIndicators;
    }

    constructor() {
        super();
        this.initSoulWildsIndicator();

        this.soulWild = this.addChild(new SoulWild());
        this.onResize();
    }

    private initSoulWildsIndicator() {
        for (let i = 0; i < 3; i++) {
            const sw = new SoulWildIndicator(i, this.levelThresholds[i]);
            this.souldWildsIndicators.push(sw);
            this.addChild(sw);
        }
    }

    public enableIntro() {
        this.souldWildsIndicators.forEach((swIndicator, indx) => {
            swIndicator.unlockNext(true, true);
        });
    }

    public forceIndicatorResets() {
        this.souldWildsIndicators.forEach((swIndicator) => {
            swIndicator.forceReset();
        });
    }

    public enableSWindicator(level: number, playRestoreSound: boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline();
        this.souldWildsIndicators.forEach((swIndicator, indx) => {
            if (!level || swIndicator.isEnabled) return;

            if (level - indx * 2 > 0) {
                swIndicator.unlockNext(true, false, playRestoreSound);
            } else {
                const remaining: number = level - swIndicator.unlockedIndex;
                for (let k = 0; k < remaining; k++) {
                    swIndicator.unlockNext(false, false, playRestoreSound);
                }
            }
        });
        return tl;
    }

    public onLevelUpIndicator(): gsap.core.Timeline {
        const tl = gsap.timeline();
        this.souldWildsIndicators.forEach((swIndicator) => {
            !swIndicator.isEnabled && tl.add(swIndicator.unlockNext(), 0);
        });
        return tl;
    }

    public deactivateIndicators(): void {
        this.souldWildsIndicators.forEach((swIndicator) => {
            if (swIndicator.isEnabled) {
                swIndicator.deactivate();
            }
        });
        if (!this.isSoulWild && Beheaded.model.gameData.collectedSouls > 0) {
            Beheaded.soundPlayer.playSound(SoundConfig.SW_INDICATOR_BAR_RESET);
        }
    }

    public hideAllProgressionBars(boosted?: boolean): void {
        if (boosted) this.souldWildsIndicators[0].showProgressionBar();
        else {
            this.souldWildsIndicators.forEach((swIndicator) => {
                swIndicator.hideProgressionBar();
            });
        }
    }

    public disableIndicator(isIntro: boolean = false): void {
        this.souldWildsIndicators.forEach((swIndicator, swIndicatorIndex) => {
            if (GameSetting.isBoostedBet) {
                if (swIndicatorIndex) swIndicator.resetRing(true, !!isIntro);
            } else {
                swIndicator.resetRing(false, !!isIntro);
            }
        });
    }

    //SW ON REELS METHODS - start from here
    public soulWildLand(pos: { reel: number; row: number }, size: number): gsap.core.Timeline {
        this.isSoulWild = true;
        return this.soulWild.show(pos, size);
    }

    public killSoulWild(): gsap.core.Timeline {
        return this.soulWild.killSoloJW();
    }

    public showWin(durr: number, size: number): gsap.core.Timeline {
        return this.soulWild.playWinAnimation(durr, size);
    }

    public applySoulWildToMult(size: number, NoWin: boolean): gsap.core.Timeline {
        return this.soulWild.moveJWToMult(size, NoWin);
    }

    public soulWildRemove(): gsap.core.Timeline {
        if (this.isSoulWild) {
            this.isSoulWild = false;
            Beheaded.baseMultiplier.setValue(1);
            return this.soulWild.remove();
        } else return gsap.timeline();
    }

    public isRestore(collectedSouls: number, currentLevel: number, swPos: boolean[][], swApplied: boolean): gsap.core.Timeline {
        const tl = gsap.timeline();

        Beheaded.souldWildController.enableSWindicator(currentLevel, true);

        let enabledSW = 0;
        Beheaded.souldWildController.SWIndicators.forEach((sw) => {
            sw.isEnabled && enabledSW++;
        });

        enabledSW > 0 && SoundManager.playSoundEffect(SoundConfig.SW_ENABLED[enabledSW - 1]);

        if (swApplied) {
            ArrayHelper.eachForEach(swPos, (reel, row) => {
                if (swPos[reel][row]) {
                    tl.add(BeheadedEngineManager.getReel(reel).getSymbol(row)?.convertToWild()!, 0);
                }
            });
        } else {
            this.setAmmSoulsCollected(collectedSouls);
        }
        return tl;
    }

    setAmmSoulsCollected(collectedSouls:number){
        let collectedSoulsUpdated: number = collectedSouls;

        this.souldWildsIndicators.forEach((sInd) => {
            if (collectedSoulsUpdated && sInd.isEnabled) {
                sInd.updateBar(collectedSoulsUpdated);
                collectedSoulsUpdated -= sInd.soulsCollected;
            }
        });
    }

    public onResize() {
        if (NolimitApplication.isLandscape) {
            this.souldWildsIndicators[0].position.set(-200, 40);
            this.souldWildsIndicators[1].position.set(-200, 182);
            this.souldWildsIndicators[2].position.set(-200, 327);
        } else {
            this.souldWildsIndicators[0].position.set(95, -215);
            this.souldWildsIndicators[1].position.set(228, -215);
            this.souldWildsIndicators[2].position.set(360, -215);
        }

        this.position = ResizeHelper.calcDim(this.dim).position;
    }
}
